import React from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  Fade,
} from "@mui/material";
import LockClockIcon from "@mui/icons-material/LockClock";
import { useTranslation } from "react-i18next";

const WARN_DURATION = 60;

export default function SessionTimeoutModal({ open, countdown, onStayLoggedIn, onLogout }) {
  const { t } = useTranslation();
  const progress = (countdown / WARN_DURATION) * 100;
  const isUrgent = countdown <= 10;

  const headerBg = isUrgent
    ? "linear-gradient(135deg, #b71c1c 0%, #e53935 100%)"
    : "linear-gradient(135deg, #e65100 0%, #fb8c00 100%)";

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      onClose={onStayLoggedIn}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 32px 64px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          background: headerBg,
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          transition: "background 0.6s ease",
        }}
      >
        <LockClockIcon sx={{ color: "white", fontSize: 30 }} />
        <Typography variant="h6" fontWeight={700} color="white" letterSpacing={0.3}>
          {t("sessionTimeout.title")}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3.5, pt: 3.5, pb: 1 }}>
        {/* Inactive message */}
        <Typography
          variant="body1"
          color="text.secondary"
          textAlign="center"
          mb={3.5}
          lineHeight={1.6}
        >
          {t("sessionTimeout.inactiveMessage")}
        </Typography>

        {/* Countdown ring */}
        <Box display="flex" justifyContent="center" mb={3.5}>
          <Box sx={{ position: "relative", width: 110, height: 110 }}>
            {/* Gray track */}
            <CircularProgress
              variant="determinate"
              value={100}
              size={110}
              thickness={4}
              sx={{ color: "grey.200", position: "absolute", top: 0, left: 0 }}
            />
            {/* Animated arc */}
            <CircularProgress
              variant="determinate"
              value={progress}
              size={110}
              thickness={4}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                color: isUrgent ? "error.main" : "warning.main",
                transition: "color 0.6s ease",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                  transition: "stroke-dashoffset 1s linear",
                },
              }}
            />
            {/* Center label */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h3"
                fontWeight={800}
                color={isUrgent ? "error.main" : "text.primary"}
                lineHeight={1}
                sx={{ transition: "color 0.6s ease" }}
              >
                {countdown}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={600} mt={0.3}>
                {t("sessionTimeout.seconds")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          mb={0.5}
          sx={{ opacity: 0.85 }}
        >
          {t("sessionTimeout.hint")}
        </Typography>
      </DialogContent>

      {/* Actions */}
      <Box sx={{ px: 3.5, pb: 3.5, pt: 2, display: "flex", gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={onLogout}
          sx={{
            borderRadius: 2,
            py: 1.3,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.9rem",
            borderWidth: 1.5,
            "&:hover": { borderWidth: 1.5 },
          }}
        >
          {t("sessionTimeout.logoutNow")}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={onStayLoggedIn}
          autoFocus
          disableElevation
          sx={{
            borderRadius: 2,
            py: 1.3,
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.9rem",
            background: "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)",
            boxShadow: "0 4px 14px rgba(25,118,210,0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
              boxShadow: "0 6px 18px rgba(25,118,210,0.5)",
            },
          }}
        >
          {t("sessionTimeout.stayLoggedIn")}
        </Button>
      </Box>
    </Dialog>
  );
}
